import { CommonUtils } from "../../util/common-utils";
import type { RankingMonthlyTabType, RankingsMonthlyTabsForm } from "./rankings-monthly-tabs-form";
import { RankingsMonthlyTabsRender } from "./rankings-monthly-tabs-render";

export class RankingsMonthlyTabsHandler {
    public form: RankingsMonthlyTabsForm;

    public constructor(form: RankingsMonthlyTabsForm) {
        this.form = form;
    }

    public bootstrap(): void {
        const tabs = this.form.getTabs();
        const panels = this.form.getPanels();
        if (tabs.length === 0 || panels.length === 0) return;

        const hashType = this.form.resolveTypeFromHash();
        const initialType = this.form.getInitialType();
        const pickedType: RankingMonthlyTabType = hashType ?? initialType ?? "product";
        this.setActiveType(pickedType, { syncHash: false });

        this.bootstrapParentTabs();
    }

    public setActiveType(type: RankingMonthlyTabType, options: { syncHash?: boolean } = {}): void {
        const { syncHash = true } = options;

        this.form.getRoot().dataset.activeTab = type;

        const tabs = this.form.getTabs();
        const panels = this.form.getPanels();
        RankingsMonthlyTabsRender.renderTypeActive(tabs, panels, type);

        if (!syncHash) return;

        const activePanel = panels.find((panel) => panel.dataset.rankingType === type);
        const panelId = activePanel?.id;
        if (!panelId) return;

        const nextHash = `#${panelId}`;
        if (location.hash !== nextHash) {
            history.replaceState(null, "", nextHash);
        }
    }

    public handleTypeTabClick(event: Event, type: string | null | undefined): void {
        if (type !== "product" && type !== "user") return;

        event.preventDefault();
        this.setActiveType(type);
    }

    public redirectToMonth(month: string): void {
        const newMonth = (month || "").trim();
        if (!/^\d{4}-\d{2}$/.test(newMonth)) return;

        const panels = this.form.getPanels();
        const activeType = this.form.getRoot().dataset.activeTab;
        const resolvedType: RankingMonthlyTabType = activeType === "user" ? "user" : "product";

        const url = new URL(window.location.href);
        url.pathname = CommonUtils.resolveUrl("/rankings/monthly");
        url.search = "";
        url.searchParams.set("month", newMonth);

        const activePanel = panels.find((panel) => panel.dataset.rankingType === resolvedType);
        const panelId = activePanel?.id;
        url.hash = panelId ? `#${panelId}` : "";

        window.location.assign(url.toString());
    }

    public shiftMonth(month: string, delta: number): string | null {
        const value = (month || "").trim();
        if (!/^\d{4}-\d{2}$/.test(value)) return null;

        const [yearRaw, monthRaw] = value.split("-");
        const year = Number(yearRaw);
        const monthIndex = Number(monthRaw) - 1;
        if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) return null;

        const date = new Date(year, monthIndex + delta, 1);
        const nextYear = date.getFullYear();
        const nextMonth = String(date.getMonth() + 1).padStart(2, "0");
        return `${nextYear}-${nextMonth}`;
    }

    public handleMonthInputChange(): void {
        const monthInput = this.form.getMonthInput();
        if (!(monthInput instanceof HTMLInputElement)) return;
        this.redirectToMonth(monthInput.value);
    }

    public handleMonthPrevClick(event: Event): void {
        const monthInput = this.form.getMonthInput();
        if (!(monthInput instanceof HTMLInputElement)) return;

        event.preventDefault();
        const nextMonth = this.shiftMonth(monthInput.value, -1);
        if (!nextMonth) return;

        monthInput.value = nextMonth;
        this.redirectToMonth(nextMonth);
    }

    public handleMonthNextClick(event: Event): void {
        const monthInput = this.form.getMonthInput();
        if (!(monthInput instanceof HTMLInputElement)) return;

        event.preventDefault();
        const nextMonth = this.shiftMonth(monthInput.value, 1);
        if (!nextMonth) return;

        monthInput.value = nextMonth;
        this.redirectToMonth(nextMonth);
    }

    public bootstrapParentTabs(): void {
        const parentTabs = this.form.getParentTabs();
        const parentPanels = this.form.getParentPanels();
        if (parentTabs.length === 0 || parentPanels.length === 0) return;

        const initialParentId = this.form.getInitialParentCategoryId();
        const fallbackParentId = parentTabs[0]?.dataset.parentCategoryId || null;
        const pickedParentId = initialParentId || fallbackParentId;
        if (!pickedParentId) return;

        RankingsMonthlyTabsRender.renderParentActive(parentTabs, parentPanels, pickedParentId);
    }

    public handleParentTabClick(event: Event, parentCategoryId: string | null | undefined): void {
        if (!parentCategoryId) return;

        event.preventDefault();

        const parentTabs = this.form.getParentTabs();
        const parentPanels = this.form.getParentPanels();
        RankingsMonthlyTabsRender.renderParentActive(parentTabs, parentPanels, parentCategoryId);
    }
}
